import {
  getMaxBirthDateInputValue,
  GUIDANCE_STYLE_OPTIONS,
  GuidanceStylePreference,
  LANGUAGE_PROTOCOL_OPTIONS,
  LanguageProtocolPreference,
  SoulMapProfileForm,
} from '@/utils/soulmap-profile';
import { useState } from 'react';
import { BirthPlaceAutocomplete } from './birth-place-autocomplete';

export interface SoulMapProfileFieldErrors {
  birthDate?: string;
  birthTime?: string;
  birthPlace?: string;
}

const LANGUAGE_PROTOCOL_QUESTION =
  'Would you like Soul Map to speak in a more spiritual or secular tone?';

/** Reads as a continuation of the question above it, not a fresh preamble. */
const GUIDANCE_STYLE_QUESTION =
  'Should it be actively soul-affirming, or more neutral and matter-of-fact?';

const LANGUAGE_PROTOCOL_LABELS: Record<LanguageProtocolPreference, string> = {
  spiritual: 'Spiritual',
  secular: 'Secular',
};

const GUIDANCE_STYLE_LABELS: Record<GuidanceStylePreference, string> = {
  'soul-affirming': 'Soul-affirming',
  neutral: 'Neutral',
};

interface ProfilePreferenceToggleProps<T extends string> {
  question: string;
  options: readonly T[];
  labels: Record<T, string>;
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
}

function ProfilePreferenceToggle<T extends string>({
  question,
  options,
  labels,
  value,
  onChange,
  disabled = false,
}: ProfilePreferenceToggleProps<T>) {
  return (
    <fieldset className="sm-profile-fieldset" disabled={disabled}>
      <legend className="sm-profile-question">{question}</legend>
      <div className="mt-3 flex gap-3">
        {options.map((option) => {
          const isActive = value === option;
          return (
            <button
              key={option}
              type="button"
              aria-pressed={isActive}
              className="sm-toggle-option flex-1"
              style={{
                fontSize: 15,
                padding: '13px 16px',
                background: isActive ? 'var(--sm-coral)' : '#fff',
                color: isActive ? '#fff' : 'var(--sm-muted)',
                borderColor: isActive ? 'var(--sm-coral)' : 'var(--sm-line)',
              }}
              onClick={() => onChange(option)}
            >
              {labels[option]}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

interface SoulMapProfileFieldsProps {
  form: SoulMapProfileForm;
  errors: SoulMapProfileFieldErrors;
  disabled?: boolean;
  onChange: (patch: Partial<SoulMapProfileForm>) => void;
  onClearError: (field: keyof SoulMapProfileFieldErrors) => void;
  /**
   * Section rules for surfaces where the heading cannot describe both halves.
   * The landing page frames them in its own intro, so it leaves these off.
   */
  showSectionHeadings?: boolean;
  /** Sits directly above the date, time and place fields, where it applies. */
  birthDetailsNote?: string;
  /** A stored birth place is still being looked up in the city catalog. */
  isResolvingBirthPlace?: boolean;
}

function SectionRule({ children }: { children: string }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <span
        className="whitespace-nowrap text-tiny font-semibold uppercase tracking-[0.14em]"
        style={{ color: 'var(--sm-muted-2)' }}
      >
        {children}
      </span>
      <span className="sm-divider" />
    </div>
  );
}

/** Shared between the consumer landing form and the in-chat confirmation modal. */
export function SoulMapProfileFields({
  form,
  errors,
  disabled = false,
  onChange,
  onClearError,
  showSectionHeadings = false,
  birthDetailsNote,
  isResolvingBirthPlace = false,
}: SoulMapProfileFieldsProps) {
  const [showBirthTimeHint, setShowBirthTimeHint] = useState(false);
  const maxBirthDate = getMaxBirthDateInputValue();

  return (
    <>
      {showSectionHeadings && (
        <SectionRule>How Soul Map speaks with you</SectionRule>
      )}

      <ProfilePreferenceToggle
        question={LANGUAGE_PROTOCOL_QUESTION}
        options={LANGUAGE_PROTOCOL_OPTIONS}
        labels={LANGUAGE_PROTOCOL_LABELS}
        value={form.languageProtocol}
        onChange={(languageProtocol) => onChange({ languageProtocol })}
        disabled={disabled}
      />

      <ProfilePreferenceToggle
        question={GUIDANCE_STYLE_QUESTION}
        options={GUIDANCE_STYLE_OPTIONS}
        labels={GUIDANCE_STYLE_LABELS}
        value={form.guidanceStyle}
        onChange={(guidanceStyle) => onChange({ guidanceStyle })}
        disabled={disabled}
      />

      {showSectionHeadings && <SectionRule>Your birth details</SectionRule>}

      {birthDetailsNote && (
        <p className="sm-profile-hint">{birthDetailsNote}</p>
      )}

      <div className="sm-profile-birth-row">
        <div className="sm-profile-field">
          <label className="sm-profile-label" htmlFor="soulmap-birth-date">
            Birth date
          </label>
          <input
            id="soulmap-birth-date"
            type="date"
            className="sm-profile-input"
            value={form.birthDate}
            min="1900-01-01"
            max={maxBirthDate}
            onChange={(event) => {
              onChange({ birthDate: event.target.value });
              onClearError('birthDate');
            }}
            disabled={disabled}
          />
          {errors.birthDate && (
            <p className="sm-profile-error">{errors.birthDate}</p>
          )}
        </div>

        <div className="sm-profile-field">
          <label className="sm-profile-label" htmlFor="soulmap-birth-time">
            Birth time
          </label>
          <input
            id="soulmap-birth-time"
            type="time"
            className="sm-profile-input"
            value={form.birthTime}
            onChange={(event) => {
              onChange({ birthTime: event.target.value });
              onClearError('birthTime');
            }}
            disabled={disabled}
          />
          {errors.birthTime && (
            <p className="sm-profile-error">{errors.birthTime}</p>
          )}
          <button
            type="button"
            className="sm-link self-start text-left"
            onClick={() => setShowBirthTimeHint((current) => !current)}
            disabled={disabled}
          >
            Not sure of your exact time?
          </button>
          {showBirthTimeHint && (
            <p className="sm-profile-hint">
              Enter your closest estimate. A rough time still helps; the more
              precise it is, the truer your map.
            </p>
          )}
        </div>
      </div>

      <BirthPlaceAutocomplete
        value={form.birthPlace}
        onChange={(birthPlace) => {
          onChange({ birthPlace });
          onClearError('birthPlace');
        }}
        disabled={disabled}
        error={errors.birthPlace}
        isResolving={isResolvingBirthPlace}
      />
    </>
  );
}
